import { FC, FormEvent, useState } from 'react';

import {
  ISaveDraftInput,
  IVersionMetadataErrors,
  TSaveDraftResult,
  hasVersionMetadataErrors,
  isNameTaken,
  splitMetadataIssues,
  validateVersionMetadata,
} from '@entities/routing/config-version';
import { useModal } from '@shared/components/Modal';

import VersionMetadataFields from './VersionMetadataFields';

interface IProps {
  // The version Save Draft was pressed on — the clone source, or the draft itself.
  baseVersionId: number;
  baseName: string;
  baseComment: string | null;
  // A draft base is saved in place; any other status is cloned into a new draft.
  isCreatingDraft: boolean;
  // Names of every other version: the draft POST refuses a duplicate, so the form
  // blocks a known collision up front.
  takenNames: string[];
  onSubmit: (input: ISaveDraftInput) => Promise<TSaveDraftResult>;
}

// Names and comments both live here: the draft POST only takes a name, and the
// comment reaches the version through the metadata PATCH the save runs next to it.
const SaveDraftModal: FC<IProps> = ({
  baseVersionId,
  baseName,
  baseComment,
  isCreatingDraft,
  takenNames,
  onSubmit,
}) => {
  const { closeModal } = useModal();
  const [name, setName] = useState(baseName);
  const [comment, setComment] = useState(baseComment ?? '');
  const [fieldErrors, setFieldErrors] = useState<IVersionMetadataErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDuplicateName = isNameTaken(name, takenNames);

  const onNameChange = (value: string) => {
    setName(value);
    setFieldErrors(({ name: _name, ...rest }) => rest);
  };
  const onCommentChange = (value: string) => {
    setComment(value);
    setFieldErrors(({ comment: _comment, ...rest }) => rest);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Enter in an input submits past the disabled button.
    if (isDuplicateName) return;

    const errors = validateVersionMetadata({ name, comment });
    if (hasVersionMetadataErrors(errors)) {
      setFieldErrors(errors);
      setError(null);
      return;
    }

    setFieldErrors({});
    setError(null);
    setIsSubmitting(true);

    let result: TSaveDraftResult;
    try {
      result = await onSubmit({ name: name.trim(), comment });
    } catch {
      // onSubmit should resolve with a result, but a rejection must still release
      // the form instead of leaving it disabled forever.
      setIsSubmitting(false);
      setError('Saving the draft failed. Please try again.');
      return;
    }

    // Only the steps whose inputs live in this form keep it open; config errors
    // belong to the banner.
    if (result.status === 'failed' && result.stage !== 'update') {
      setIsSubmitting(false);
      const { fields, general } = splitMetadataIssues(result.issues);
      setFieldErrors(fields);
      setError(general);
      return;
    }

    closeModal();
  };

  return (
    <form className='routing-version-form' onSubmit={handleSubmit} noValidate>
      <h3 className='routing-version-form__title'>
        {isCreatingDraft ? 'Save as a new draft' : 'Save draft'}
      </h3>
      <p className='routing-version-form__hint'>
        {isCreatingDraft
          ? `v${baseVersionId} stays as it is — your changes are saved into a new draft version.`
          : `Your changes are saved into draft v${baseVersionId}.`}
      </p>

      <VersionMetadataFields
        idPrefix='routing-draft'
        nameLabel='Draft name'
        commentPlaceholder='What changed in this draft?'
        name={name}
        comment={comment}
        onNameChange={onNameChange}
        onCommentChange={onCommentChange}
        takenNames={takenNames}
        errors={fieldErrors}
        disabled={isSubmitting}
      />

      {error && (
        <p className='routing-version-form__error routing-version-form__error--form' role='alert'>
          {error}
        </p>
      )}

      <div className='routing-version-form__btns'>
        <button
          type='button'
          className='secondary-btn'
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button type='submit' disabled={isSubmitting || isDuplicateName}>
          {isSubmitting ? 'Saving…' : 'Save Draft'}
        </button>
      </div>
    </form>
  );
};

export default SaveDraftModal;
